import { Bell, Wifi, WifiOff, Check, X, Settings, Volume2, VolumeX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useNotificationContext } from '../contexts/NotificationContext'
import type { OrderNotification } from '../types'

// Format relative time
function formatRelativeTime(timestamp: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  const now = Date.now()
  const diff = now - timestamp
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)

  if (minutes < 1) return t('notifications.justNow')
  if (minutes < 60) return t('notifications.minutesAgo', { count: minutes })
  if (hours < 24) return t('notifications.hoursAgo', { count: hours })
  return new Date(timestamp).toLocaleDateString()
}

// Notification item component
function NotificationItem({
  notification,
  onAcknowledge,
  onRemove,
  t,
}: {
  notification: OrderNotification
  onAcknowledge: (id: string) => void
  onRemove: (id: string) => void
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg transition-colors',
        notification.acknowledged
          ? 'bg-muted/50'
          : 'bg-orange-50 dark:bg-orange-950/30 border-l-4 border-orange-500'
      )}
    >
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm',
          !notification.acknowledged && 'font-medium'
        )}>
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {formatRelativeTime(notification.timestamp, t)}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!notification.acknowledged && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onAcknowledge(notification.id)}
            title={t('notifications.markAsRead')}
          >
            <Check className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(notification.id)}
          title={t('notifications.remove')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// Connection status indicator
function ConnectionStatusIndicator({ status, t }: { status: string; t: (key: string) => string }) {
  const statusConfig = {
    connected: { icon: Wifi, color: 'text-green-500', label: t('notifications.connected') },
    connecting: { icon: Wifi, color: 'text-yellow-500 animate-pulse', label: t('notifications.connecting') },
    disconnected: { icon: WifiOff, color: 'text-muted-foreground', label: t('notifications.disconnected') },
    error: { icon: WifiOff, color: 'text-destructive', label: t('notifications.error') },
  }

  const config = statusConfig[status as keyof typeof statusConfig]
  const Icon = config.icon

  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className={cn('h-3 w-3', config.color)} />
      <span className="text-muted-foreground">{config.label}</span>
    </div>
  )
}

export function NotificationBadge() {
  const { t } = useTranslation('generalSettings')
  const {
    notifications,
    unreadCount,
    settings,
    connectionStatus,
    acknowledgeNotification,
    acknowledgeAll,
    removeNotification,
    clearAll,
    updateSettings,
    notificationPermission,
    browserNotificationsSupported,
    requestNotificationPermission,
    reconnect,
  } = useNotificationContext()

  const hasUnread = unreadCount > 0

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={hasUnread ? t('notifications.ariaLabelUnread', { count: unreadCount }) : t('notifications.ariaLabel')}
        >
          <Bell className={cn('h-5 w-5', hasUnread && 'animate-bounce')} />
          {hasUnread && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-96 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{t('notifications.title_badge')}</h3>
            <ConnectionStatusIndicator status={connectionStatus} t={t} />
          </div>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              onClick={acknowledgeAll}
              className="text-xs"
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>

        {/* Notifications list */}
        <ScrollArea className="h-[300px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-8">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onAcknowledge={acknowledgeNotification}
                  onRemove={removeNotification}
                  t={t}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        <Separator />

        {/* Settings */}
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t('notifications.settings')}</span>
            </div>
          </div>

          {/* Sound toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {settings.soundEnabled ? (
                <Volume2 className="h-4 w-4 text-muted-foreground" />
              ) : (
                <VolumeX className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm">{t('notifications.sound')}</span>
            </div>
            <Switch
              checked={settings.soundEnabled}
              onCheckedChange={(soundEnabled) => updateSettings({ soundEnabled })}
            />
          </div>

          {browserNotificationsSupported && (
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <label className="text-sm">{t('notifications.browserNotificationsEnabled')}</label>
                {notificationPermission === 'denied' && (
                  <p className="text-xs text-destructive">
                    {t('notifications.permissionBlocked')}
                  </p>
                )}
              </div>
              <Switch
                checked={settings.browserNotificationsEnabled}
                onCheckedChange={async (browserNotificationsEnabled) => {
                  if (browserNotificationsEnabled && notificationPermission !== 'granted') {
                    const permission = await requestNotificationPermission()
                    if (permission === 'granted') {
                      updateSettings({ browserNotificationsEnabled: true })
                    }
                  } else {
                    updateSettings({ browserNotificationsEnabled })
                  }
                }}
                disabled={notificationPermission === 'denied'}
              />
            </div>
          )}

          {/* Reconnect button (shown on error) */}
          {connectionStatus === 'error' && (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={reconnect}
            >
              <Wifi className="h-4 w-4 mr-2" />
              {t('notifications.reconnect')}
            </Button>
          )}

          {/* Clear all button */}
          {notifications.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="w-full text-muted-foreground"
              onClick={clearAll}
            >
              {t('notifications.clearAll')}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
